"use client";

import { type JSX, useState } from "react";
import { type LinkField, Text } from "@sitecore-content-sdk/nextjs";
import Button, { buttonVariants, getIconDetails } from "../../../atoms/Button";
import { ICONS } from "../../../variables/icons";
import useIsMobile from "../../../hooks/useIsMobile";
import { extractHiddenBlock } from "../../../utils/styles";
import { NoDataFallback } from "../../../utils/NoDataFallback";
import {
  LinkListProps,
  LinkListResult,
} from "components/sxa/link-list/link-list.props";
import { POSITIONAL_CLASSES, SPACING_CLASSES } from "src/variables/styles";

const getPositionClassForTextContent = (cls: string) => {
  if (cls == "position-center") return "text-left after:mx-auto";
  if (cls == "position-right") return "text-right after:ml-auto";
  if (cls == "position-left") return "text-left after:mr-auto";
};

const buildItemClassName = (index: number, total: number): string => {
  const parity = (index + 1) % 2 === 0 ? " even" : " odd";
  const first = index === 0 ? " first" : "";
  const last = index + 1 === total ? " last" : "";
  return `item${index}${parity}${first}${last}`;
};

const collectLinks = (props: LinkListProps): LinkListResult[] => {
  const results = props.fields?.data?.datasource?.children?.results ?? [];
  return results.filter(
    (result): result is LinkListResult => !!result?.field?.link,
  );
};

interface LinkListItemProps {
  index: number;
  total: number;
  field: LinkField;
  componentbuttonType: string;
}

const LinkListItem = ({
  index,
  total,
  field,
  componentbuttonType,
}: LinkListItemProps): JSX.Element => {
  const itemClass = buildItemClassName(index, total);
  const buttonType = field?.value?.class
    ? field?.value?.class
    : componentbuttonType;

  return (
    <li
      className={`linklist-component ${itemClass} block list-none mb-[16px] min-lg:mb-[0px] self-center`}
    >
      <span data-role="link-list-component" className="block">
        <Button
          buttonType={buttonType}
          field={field}
          iconName="CHEVRON_BLACK_RIGHT"
          className={
            buttonType === "numbers"
              ? "w-full justify-between! py-[20px] lg:py-[28px] font-medium"
              : "justify-self-start"
          }
          data-role="link"
        />
      </span>
    </li>
  );
};

interface FooterLinkListItemProps {
  index: number;
  total: number;
  field: LinkField;
  style?: string;
  icon?: string;
  defaultIconName?: string;
}

const FooterLinkListItem = ({
  index,
  total,
  field,
  style,
  icon,
  defaultIconName,
}: FooterLinkListItemProps): JSX.Element => {
  const itemClass = buildItemClassName(index, total);
  const linkClass = field?.value?.class;

  const iconDetails = linkClass
    ? getIconDetails(linkClass, defaultIconName)
    : { jssIconField: { value: icon }, iconPosition: "right" };

  return (
    <li className={`${itemClass} list-none mb-[0px]`}>
      <Button
        buttonType={linkClass}
        field={field}
        {...iconDetails}
        className={`py-[0px]! px-[0px]! mr-[0px]! max-lg:mb-[16px]! lg:mb-[20px] gap-[10px] leading-[19.2px]! min-w-auto! min-h-auto! max-lg:text-text-gray lg:text-white font-normal lg:hover:underline active:underline max-lg:justify-end! justify-self-start ${style ?? ""} ${linkClass ? "" : "[&_span:last-child]:pt-0"}`}
        data-role="link"
      />
    </li>
  );
};

export const Default = (props: LinkListProps): JSX.Element => {
  console.log("---->[LinkList] Component", props.fields);

  const { fields, params } = props;

  // Content authors can pick the FooterList layout via this rendering param.
  if (params?.FieldNames === "FooterList") {
    return <FooterList {...props} />;
  }

  const datasource = fields?.data?.datasource;
  if (!datasource) {
    return <NoDataFallback componentName="Link List" />;
  }
  const stylesArray = props?.params?.Styles
    ? props.params.Styles?.split(" ")
    : [];
  const buttonType =
    stylesArray.length > 0
      ? buttonVariants.find((cls) => stylesArray.includes(cls)) || "numbers"
      : "numbers";

  const id = params?.RenderingIdentifier;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const links = collectLinks(props);

  const styleTokens = params?.Styles?.split(" ") ?? [];
  const resolvedPosition = styleTokens.find((cls) =>
    POSITIONAL_CLASSES.includes(cls),
  );
  const resolvedSpacing = styleTokens.filter((cls) =>
    SPACING_CLASSES.includes(cls),
  );
  const filteredStyles = styleTokens?.filter(
    (cls) => !(resolvedSpacing?.includes(cls) || cls == resolvedPosition),
  );

  return (
    <div
      className={`linklist-component mb-[var(--global-component-spacing)] relative p-0 bg-transparent ${resolvedPosition} ${resolvedSpacing?.join(" ")} ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-role="link-list-component"
    >
      <div className="px-4">
        {datasource.field?.title && (
          <Text
            tag="h2"
            className={`text-text-gray ${getPositionClassForTextContent(resolvedPosition ?? "position-center")} mb-[0] pb-[20px] font-bold tracking-[0.4px] after:block after:w-[50%] lg:after:w-[17%] after:content-[] after:pt-[15px] after:border-b-[2px] after:border-[var(--color-teal)]`}
            field={datasource.field.title}
            data-testid="list-heading"
          />
        )}
        <ul
          className={`bg-transparent mt-[8px] mb-[16px] lg:grid lg:gap-6 ${filteredStyles?.join(" ") ?? ""}`}
          data-testid="list-items"
        >
          {links.map((result, index) => (
            <LinkListItem
              key={`linklist-item-${result?.field?.link?.value?.text}`}
              index={index}
              total={links.length}
              field={result.field.link}
              componentbuttonType={buttonType}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export const FooterList = (props: LinkListProps): JSX.Element => {
  console.log("---->[LinkList] Component", props?.fields);

  const { fields, params } = props;
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const datasource = fields?.data?.datasource;
  if (!datasource) {
    return <NoDataFallback componentName="Link List" />;
  }

  const id = params?.RenderingIdentifier;
  const styles = `${params?.Styles ?? ""}`.trimEnd();
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const listHeadingStyle = isExpanded
    ? "max-lg:rounded-t-[6px] max-lg:bg-ecolab-blue max-lg:bg-icon-minus-white"
    : "max-lg:rounded-[6px] max-lg:bg-white max-lg:bg-icon-plus-ecolab-blue";
  const listUlStyle = isExpanded
    ? "max-lg:transition-all max-lg:duration-300 max-lg:ease-in-out max-lg:max-h-[500px] max-lg:overflow-hidden max-lg:visible"
    : "max-lg:transition-all max-lg:duration-300 max-lg:ease-in-out max-lg:max-h-0 max-lg:overflow-hidden max-lg:invisible";

  const rightArrowIcon = isMobile
    ? ICONS.CHEVRON_BLUE_RIGHT_FOOTER
    : ICONS.CHEVRON_WHITE_RIGHT_FOOTER;
  const defaultIconName = isMobile
    ? "CHEVRON_BLUE_RIGHT_FOOTER"
    : "CHEVRON_WHITE_RIGHT_FOOTER";

  const links = collectLinks(props);

  const handleHeadingToggle = () => {
    if (isMobile) {
      setIsExpanded((prev) => !prev);
    }
  };

  return (
    <div
      className={`linklist-footerlist-component relative px-[16px] ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-role="link-list-component"
    >
      <div className="max-lg:mb-[10px]">
        {datasource.field?.title && (
          <button
            type="button"
            className={`w-full block cursor-pointer max-lg:py-[14px] max-lg:pr-[40px] max-lg:pl-[20px] lg:mb-[32px] max-lg:bg-no-repeat max-lg:bg-[position:center_right_15px] max-lg:bg-size-[20px 20px] ${listHeadingStyle}`}
            onClick={handleHeadingToggle}
            data-testid="footer-list-heading-container"
          >
            <Text
              tag="h2"
              className={`${
                isExpanded ? "max-lg:text-white" : "max-lg:text-text-gray"
              } lg:text-white text-left text-[22px] mt-[20px] mb-[10px] font-medium leading-[24.2px] ${styles}`}
              field={datasource.field.title}
              data-testid="footer-list-heading"
            />
          </button>
        )}
        <div className={`${listUlStyle} mb-[20px] lg:block!`}>
          <ul
            className="px-[0px] max-lg:px-[20px] max-lg:pt-[20px] max-lg:pb-[1px] max-lg:mt[-10px] max-lg:mb[10px] max-lg:bg-white max-lg:rounded-b-[6px] max-lg:rounded-t-none"
            data-testid="footer-list-items"
          >
            {links.map((result, index) => (
              <FooterLinkListItem
                key={`footer-linklist-item-${result?.field?.link?.value?.text}`}
                index={index}
                total={links.length}
                field={result.field.link}
                style={styles}
                icon={rightArrowIcon}
                defaultIconName={defaultIconName}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
